import React, { useState, useEffect } from 'react';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import GanttChartPlanner from '././GanttChartPlan';
import './ProjectPlanning.scss';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import ScenarioSwitcher from './ScenarioSwitcher';
import { ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import { useTranslation } from 'react-i18next';

interface ProjectPlanningProps {
  projectId: string;
  scenarioId: string;
  setScenarioId: (id: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const ProjectPlanning: React.FC<ProjectPlanningProps> = ({ projectId, scenarioId, setScenarioId, onDirtyChange }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios, setScenarios } = useProjectScenarios();
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const provider = new ProjectsScenariosRestDataProvider();
  const scenario = scenarios.find(s => s.id === scenarioId) || scenarios[0];
  // Compute phaseEstimateHours from scenario.estimateItems
  const phaseEstimateHours: { [phaseId: string]: number } = React.useMemo(() => {
    if (!scenario || !scenario.estimateItems) return {};
    const sums: { [phaseId: string]: number } = {};
    scenario.estimateItems.forEach(item => {
      sums[item.phaseId] = (sums[item.phaseId] || 0) + (item.hours * item.multiplier || 0);
    });
    return sums;
  }, [scenario]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Save handler for ScenarioSwitcher
  const handleSave = async () => {
    if (!scenario.id) return; 
    setSaving(true);
    setIsDirty(false);
    try {
      await provider.update(scenario.id, scenario);
    } finally {
      setSaving(false);
    }
  };

  // Example: set isDirty to true on any user change
  const handleChange = (/* ... */) => {
    setIsDirty(true);
    // ...existing change logic...
  };

  // Handle dirty changes from GanttChartPlanner
  const handleGanttDirtyChange = (dirty: boolean) => {
    setIsDirty(dirty);
  };

  return (
    <>
      <ScenarioSwitcher
        scenarioId={scenario?.id}
        setScenarioId={setScenarioId}
        onSave={handleSave}
        saving={saving}
      />
      <SubsectionPanel title={t('screens:projectPlanning.title')} className="gantt-section-panel">
        {scenario ? (
          <GanttChartPlanner scenarioId={scenario.id as string} onDirtyChange={handleGanttDirtyChange} />
        ) : (
          <div>{t('screens:projects.noScenarioSelected')}</div>
        )}
      </SubsectionPanel>
    </>
  );
};

export default ProjectPlanning;
